/**
 * The shape of GET /api/schema.
 *
 * These types mirror best/services/schema.py, which is the single description
 * of what BEST can edit.  Nothing in this app should ever hard-code a field
 * list: the whole point of the registry is that adding risks or use cases to
 * the back end makes them appear here, and the UI follows without being
 * touched.
 */

/** How a field is written in SysML, which decides which control renders it. */
export type FieldKind =
  | 'doc'
  | 'text'
  | 'enum'
  | 'reference'
  | 'text_list'
  | 'reference_list'
  | 'name'
  | 'identifier'
  | 'type'
  | 'metadata_list'
  | 'flag'
  | 'conjugate'
  | 'multiplicity'
  /**
   * A number with a unit, written to the model UNQUOTED.
   *
   * Its own kind rather than `text` because the difference is not cosmetic:
   * `text` writes a string literal, so editing a mass through BEST produced
   * `:>> dryMass = "3.5 [SI::kg]";` - invalid for an `ISQ::MassValue`, and
   * invisible to every check, because a quoted string re-parses cleanly and
   * BEST read its own value back happily.
   *
   * Send a BARE NUMBER. The unit belongs to the field (see `FieldSchema.unit`)
   * and the server composes the quantity, so nobody types a bracket.
   */
  | 'quantity'

export interface FieldSchema {
  name: string
  kind: FieldKind
  /**
   * The profile feature this field binds, e.g. `dryMass`, `centerOfGravity.x`.
   *
   * The PATH is the model's own structure, and it is the only thing that says
   * `cg_x`, `cg_y` and `cg_z` are three components of one vector rather than
   * three unrelated numbers that happen to share a prefix. Group on this, never
   * on the field name: a list of groupings written here would be a parallel
   * list the model has to wait on, which is precisely what storing values by
   * feature path stopped being necessary.
   *
   * `''` where the field binds a column rather than a feature.
   */
  feature: string
  label: string
  help: string
  is_list: boolean
  /**
   * The unit a `quantity` field is stated in, e.g. `SI::kg`. `''` on every
   * other kind, so there is one shape to read.
   *
   * Show this beside the input rather than asking for it: the library declares
   * `dryMass : ISQ::MassValue`, so the unit is a property of the FIELD, not a
   * choice the typist makes. Building the control against `unit` rather than
   * against the field's name is also what lets a fourth quantity added to the
   * profile appear here with no change on this side.
   */
  unit: string
  /**
   * Read-only, and computed rather than stated.
   *
   * The write path REFUSES a derived field rather than ignoring it, so an
   * editable control on one is a promise the API breaks - a 200 that changed
   * nothing would be the worst of the three outcomes. Render these read-only
   * and label them, for everyone, regardless of permission.
   *
   * `on_part`, `verification.owner`, the interface's `declared_as` and - since
   * issue #42, when the entry succession started deriving it - `is_initial`.
   */
  derived: boolean
  /** The enumeration that fixes this field's values, when the library does. */
  enum_type: string
  /** The abstract type each program specialises with its own enum. */
  base_type: string
  /** For references: the entity kind the far end must be. */
  target: string
  /**
   * A value can be added to this enum through `api.addSchemaChoice`, with no
   * entity named - so the create form can offer it before the row exists.
   *
   * True for a requirement's model-declared enums (`budget`) and nothing else:
   * NOT `segment` or `subsystem`, which are enums with a base type too but need
   * packages and a code beside a list entry, and not a part's, which add through
   * the part route. Read this, never `base_type`, to decide whether to offer
   * the add. Optional only so a hand-built field in a test need not state it.
   */
  can_add_choice?: boolean
  /**
   * A `text` field that offers the values already in use - a requirement's
   * `category`. NOT an enum: anything may be typed. The list is there so a
   * grouping gets spelled the same way twice.
   */
  suggested?: boolean
}

export interface NamedChoice {
  name: string
  label: string
}

/**
 * A declared type a new element may be given, and what choosing it MEANS.
 *
 * The three extras are not decoration. A requirement's level used to be read
 * out of the type's name - the '3' in 'L3Requirement' - and an abstract
 * requirement type has no digit in it, so the front end was carrying a second,
 * now-wrong implementation of a rule the loader already applies. `fields` and
 * `template` are the same story for a form: which controls this type wants, and
 * whether its statement is generated rather than typed.
 */
export interface TypeChoice extends NamedChoice {
  /** L0-L4, or null for a kind that has no levels. */
  level: number | null
  /** Model-declared fields this type carries, by field name. */
  fields: string[]
  /** The statement template, when a usage's statement is GENERATED. */
  template: string
  /**
   * The sort of thing this type makes, for a kind whose types fall into a few:
   * a verification type is a `case`, a `plan` or a `campaign`. Absent or `''`
   * elsewhere. For a verification type, `fields` is the COMPLETE list of that
   * sort's field names rather than only what the type adds.
   */
  group?: string
}

export interface KindSchema {
  name: string
  plural: string
  label: string
  help: string
  keyword: string
  can_create: boolean
  types: TypeChoice[]
  /**
   * The types a new TYPE may be built from, which is NOT the same list.
   *
   * `types` is what a requirement can BE, so it holds only what reaches a
   * level. A mixin like MassRequirement reaches none by design - it carries a
   * template and an attribute and leaves the level to whatever it is combined
   * with - so it appears only here.
   */
  base_types: Omit<TypeChoice, 'level'>[]
  default_type: string
  type_required: boolean
  /** Kinds whose keyword is itself a choice, e.g. item def / port def. */
  keywords: NamedChoice[]
  fields: FieldSchema[]
}

/** One attribute a requirement type declares. */
export interface RequirementTypeAttribute {
  name: string
  declared_type: string
  description: string
}

/**
 * One abstract requirement type, as the types listing reports it.
 *
 * NOT the same shape as `TypeChoice`, and deliberately: a picker needs what is
 * usable, and a screen offering Delete needs provenance and usage. Putting the
 * second on the payload every page reads would make every page carry it.
 */
export interface RequirementTypeRow {
  name: string
  identifier: string | null
  qualified_name: string
  level: number | null
  /** The types it specializes, as WRITTEN in the model. */
  specializes: string[]
  /** The attributes it declares ITSELF - not the ones it inherits. */
  attributes: RequirementTypeAttribute[]
  /** What its requirements render from, which may come from a base. */
  template: string
  /** What this def states itself. Empty when the template above is inherited. */
  own_template: string
  /** A library type is shown and cannot be changed. */
  in_library: boolean
  /** The requirements typed by it. Non-empty means it cannot be deleted. */
  used_by: string[]
}

/**
 * An interface TYPE (issue #52): an abstract interface def a program builds its
 * interfaces from - `CCSDSInterface :> SoftwareInterface` - or one of the
 * library's three disciplines.
 */
export interface InterfaceTypeRow {
  name: string
  identifier: string | null
  qualified_name: string
  /** The types it specializes, as WRITTEN in the model. */
  specializes: string[]
  /** Software, Electrical, Mechanical - every one it reaches. */
  disciplines: string[]
  /** The attributes it declares ITSELF. */
  attributes: RequirementTypeAttribute[]
  /** Every attribute a def built from it may state, own and inherited. */
  carries: string[]
  description: string
  in_library: boolean
  /** What builds on it. Non-empty means it cannot be deleted. */
  used_by: string[]
}

/** One pin of a connector, with where its declaration and its function came from. */
export interface PinRow {
  name: string
  /** The port def that declares the pin - the connector type, usually. */
  declared_by: string
  /** What it carries; empty is a spare. */
  function: string
  /** The port def the function is assigned on, or null for none. */
  function_from: string | null
}

/** A port def's pinout, read across its specialization chain. */
export interface Pinout {
  identifier: string
  connector_type: string
  /** This port def, then each one it specializes. */
  chain: string[]
  pins: PinRow[]
}

/** A feature a new port def declares: `out item telemetry : Telemetry`. */
export interface FeatureDraft {
  name: string
  keyword: string
  direction: string
  type_name: string
  conjugate?: boolean
}

export interface Schema {
  kinds: KindSchema[]
  field_kinds: string[]
}

/** A row as any listing returns it: the fixed columns, plus the kind's fields. */
export interface EntityRow {
  kind: string
  id: string
  name: string
  qualified_name: string
  line: number
  [field: string]: unknown
}

export interface Page<T> {
  kind: string
  total: number
  limit: number
  offset: number
  items: T[]
}

/**
 * The parts listing, which carries one thing beyond the rows.
 *
 * `common_attributes` names the attributes declared on a type EVERY part in
 * this program reaches - the ones a listing can give a column to without it
 * being blank on all but one row. It is the SERVER'S answer because it is read
 * from the model: a program whose parts share a base of their own gets that
 * base's attributes, and nothing on this side names `EchelonPart`.
 *
 * The names are ATTRIBUTES, not fields: `operationalTemp`, never
 * `operational_temp_min`. A band is two columns and one attribute, and the
 * feature path is what says so.
 *
 * Optional, because an older server does not send it - and a wide table is a
 * great deal better than an empty one.
 */
/**
 * One quantity kind a new attribute may be declared as.
 *
 * `unit` is what choosing this kind gets you - `SI::W` for power - which is why
 * the create form has no unit field. `dimensionless` distinguishes a ratio,
 * which genuinely HAS no unit, from one nobody has stated: a form that cannot
 * tell them apart shows a blank where it should say so.
 */
export interface AttributeTypeChoice {
  name: string
  label: string
  unit: string
  dimensionless: boolean
  /** Text rather than a quantity (issue #52): a protocol, a connector family. */
  text?: boolean
}

export interface PartsPage extends Page<PartRow> {
  common_attributes?: string[]
  /**
   * Every part def a new definition may specialize, by name.
   *
   * NOT the same as the rows: the library's part defs - `EchelonPart` and its
   * subtypes - are where every quantity in the profile is declared, and they
   * are not projected, so a picker built from `items` offered a new definition
   * nothing to specialize but its siblings. A part def that specializes nothing
   * carries no profile attribute at all, so a mass typed onto it is stored and
   * then skipped by every rollup.
   */
  base_types?: string[]
}

export interface PackageRow {
  qualified_name: string
  name: string
  depth: number
  /**
   * The file a new declaration in this package lands in: its HOME declaration,
   * the file named for it, when a split program declares it in several.
   */
  path?: string
}

/**
 * Where a new requirement goes and what to call it.
 *
 * 'observed' means the answer was read off requirements that already exist and
 * is almost certainly right. 'package' means nothing is filed under this scoping
 * yet but a package for it exists, and the ID's code is that package's short
 * name. 'guessed' means there was nothing to learn from, and both the ID and
 * the package want checking.
 */
export interface Suggestion {
  identifier: string
  package: string
  prefix: string
  next_number: number
  source: 'observed' | 'package' | 'guessed'
  based_on: string
}

/**
 * What GET /api/version answers - issue #83. No sign-in needed.
 *
 * `version` is `YYYY.MM.Rev`, always. `commit` is the first eight characters of
 * `commit_full`; both are null when the server was not told which commit it was
 * built from (a developer's run outside a git checkout).
 */
export interface VersionInfo {
  version: string
  commit: string | null
  commit_full: string | null
  built_at: string | null
}

// ---------------------------------------------------------------------------
// The read-only views. These are hand-shaped rather than registry-driven,
// because a flowdown tree and a coverage roll-up are questions about the model
// as a whole rather than about one entity kind.
// ---------------------------------------------------------------------------

export interface Summary {
  requirements: number
  levels: Record<string, number>
  unresolved: number
  verified: number
  unverified: number
  orphans: number
  cases: number
  plans: number
  campaigns: number
  satisfy_links: number
  interfaces: number
}

export type CoverageAxis = 'subsystem' | 'segment' | 'level' | 'status' | 'test_level'

/**
 * One group of the coverage roll-up.
 *
 * 'key' is always a string once it has crossed JSON: a level of 2 arrives as
 * "2", and a requirement with no subsystem arrives under "(unset)" rather than
 * as null. The API's own shape is a map; api.coverage flattens it to this.
 */
export interface CoverageGroup {
  key: string
  total: number
  verified: number
  unresolved: number
}

// ---------------------------------------------------------------------------
// The requirement status report (GET /api/report/status?fmt=json).
//
// Typed from the shape documented in src/best/metamodel/status_report.py
// (report_data, format_version 3), and ONLY what the dashboard reads: the
// colours and the three status tables. The report carries NUMBERS AND STATUSES
// ONLY at detail=summary - no requirement id, statement or package - and the
// dashboard never asks for the other detail.
// ---------------------------------------------------------------------------

/**
 * How one status is drawn: the one list of colours the report's page is drawn
 * in (meta.status_styles), so the dashboard copies no hex value.
 *
 * `status` is null for the last item, "any other status": the colour of a
 * status the model declares beyond the listed ones. `text` is the colour that
 * reads on `fill`.
 */
export interface StatusStyle {
  status: string | null
  label: string
  fill: string
  text: string
  /** The same pair for a dark page: the same hue, stepped for a dark surface. */
  fill_dark: string
  text_dark: string
}

export interface StatusCount {
  status: string
  count: number
}

export interface StatusTableRow {
  label: string
  counts: StatusCount[]
  total: number
}

/** One group-by of the requirements: a row per level, subsystem or segment. */
export interface StatusTable {
  row_header: string
  statuses: string[]
  rows: StatusTableRow[]
  totals: StatusCount[]
  total: number
}

export interface StatusReport {
  meta: { format_version: number; status_styles: StatusStyle[] }
  headline: { total: number; unresolved: number }
  summary: {
    by_level: StatusTable
    by_subsystem: StatusTable
    /** Requirements that state a segment and NO subsystem. */
    by_segment: StatusTable
  }
}

/** What narrows the report. An empty or missing list means no restriction. */
export interface StatusReportFilters {
  levels?: string[]
  subsystems?: string[]
  segments?: string[]
}

export interface Finding {
  check: string
  severity: string
  element: string
  message: string
  line: number
  path: string
}

/** One term in the glossary, as GET /api/glossary answers it. */
export interface GlossaryEntry {
  term: string
  /** What an acronym expands to. Empty when the term is not an acronym. */
  stands_for: string
  meaning: string
  /** The section it is shown under. Empty when it has none. */
  group: string
  /** Where it was defined: the shared base set, or this program's own file. */
  scope: 'base' | 'program'
  /** True on a program entry that replaces a base entry with the same term. */
  overrides_base: boolean
  /** The file it was read from. */
  source: string
}

/** A glossary line that could not be read, and why. */
export interface GlossaryProblem {
  source: string
  line: number
  reason: string
}

/** The merged glossary: base entries not overridden, plus the program's own. */
export interface GlossaryPage {
  /** The program's name, or null when no program is current. */
  program: string | null
  /** Already merged and sorted by the server. */
  entries: GlossaryEntry[]
  /** The order to show sections in; an empty name (no group) comes last. */
  groups: string[]
  problems: GlossaryProblem[]
}

export interface FlowdownRow extends EntityRow {
  depth: number
}

export interface DesignRow {
  depth: number
  name: string
  id: string
  qualified_name: string
  type_name: string
  multiplicity: string
  is_definition: boolean
  /**
   * What the part IS, read from the model's own base types.
   *
   * `is_system` marks the part `EchelonSystem` names as the one system figures
   * are reported for, and `is_subsystem` marks the level budgets are held at.
   * Both replace guesses the page used to make - the root with the most
   * descendants, and the parts one level inside it - which agree with these on
   * a model shaped like the example and are silently wrong on one that is not.
   *
   * Unlike `is_physical` these carry to a USAGE through its definition, because
   * the design tree is mostly usages.
   */
  is_system: boolean
  is_subsystem: boolean
  ports: { name: string; type_name: string; is_conjugate: boolean }[]
  /**
   * Rolled-up quantities for this row, keyed by column.
   *
   * On the containment tree rather than only on a detail, because "what does
   * this subtree weigh" is the question somebody reading a decomposition
   * actually has - and answering it per row is what makes the tree a budget.
   */
  totals: Record<string, PartTotal>
  /**
   * The budget check for this row, keyed the same way `totals` is.
   *
   * ABSENT where the part has no budget for that quantity. `unbudgeted` is a
   * fifth status the server never sends, because "nobody has given this a
   * figure" is not a verdict about compliance and showing it beside real ones
   * invites reading it as one.
   *
   * A usage carries its DEFINITION's answer verbatim, numbers and all. A
   * budget is stated on a definition and is about that definition, so checking
   * `part seekers : Seeker[4]` against a per-seeker budget would report a
   * breach invented by the multiplicity.
   */
  budgets: Record<string, BudgetCheck>
}

export interface SourceFileRow {
  id: number
  path: string
  dialect: string
  bytes: number
  sha256: string
  imported_at: string | null
}

/**
 * One element in the model tree, as GET /api/elements answers it.
 *
 * Flat rows carrying a depth, the same shape /api/flowdown and /api/design
 * use, because that is what the database produces cheaply and rebuilding the
 * nesting on this side is already a solved problem.
 *
 * `kind` is 'package', a registry kind name, or 'element' for something BEST
 * stores but does not project. `plural` is the registry plural, so a row can be
 * linked to /{plural}/{id}; it is null exactly when nothing lists that kind,
 * which is also when there is no form to edit it with.
 */
export interface ElementRow {
  depth: number
  kind: string
  plural: string | null
  name: string
  id: string
  qualified_name: string
  type_name: string
  path: string
  line: number
  /**
   * True for an element in the shared Echelon library.
   *
   * It travels so the UI can REFUSE an edit with a reason rather than hide the
   * row. A program declaring into the library was a real defect, and the fix
   * was to guard the destination rather than the spelling.
   */
  is_library: boolean
}

/**
 * One element other than the deleted one, that a delete changes or leaves.
 *
 * `relation` is the kind of link (`source`, `cross_reference`, `allocated_to`,
 * `exhibited_by`, `transition`, ...); `detail` is one sentence for a person that
 * starts after the identifier ("loses MO_1 as a source").
 */
export interface DeleteEffect {
  relation: string
  kind: string
  identifier: string
  detail: string
  /** A requirement this delete leaves with no source at all. */
  orphaned: boolean
}

/**
 * Why an element is in the removed list: it is the one deleted (`itself`), it is
 * written inside it (`inside`), it is a usage of a definition being deleted
 * (`instance`), or it cannot exist without something being deleted (`depends`,
 * with `because` saying what).
 */
export type RemovalReason = 'itself' | 'inside' | 'instance' | 'depends'

/**
 * What deleting an element does, from GET /api/{plural}/{id}/delete-impact.
 *
 * Three groups, and the dialog keeps them apart: `removes` is deleted,
 * `unlinks` is edited to drop a link and otherwise stays, `kept` is a reference
 * the other element requires, left exactly as written. The delete then does
 * what this said; the same shape comes back from DELETE.
 */
export interface DeleteImpact {
  kind: string
  identifier: string
  name?: string
  removes: Array<{ kind: string; identifier: string; reason: RemovalReason; because?: string }>
  unlinks: DeleteEffect[]
  kept: DeleteEffect[]
  /** The requirements `unlinks` leaves with no source, by ID. */
  orphaned: string[]
}

/** What DELETE answered: the labels removed, then what was unlinked and kept. */
export interface DeleteResult {
  kind: string
  identifier: string
  /** The deleted element first, then everything that went with it. */
  removed: string[]
  unlinked: DeleteEffect[]
  kept: DeleteEffect[]
  orphaned: string[]
}

/**
 * A part as its listing answers it.
 *
 * `is_definition` is a column rather than a registry field - definition or
 * usage follows from how the declaration is written, not from an edit - so it
 * had reached the browser nowhere at all, and the Parts table listed both
 * together with nothing to tell them apart.
 *
 * `is_contained` says the row is already visible inside another part's pane, so
 * a definitions-only listing may drop it. It is NOT the same as "is a usage":
 * the root instance of a design is a usage owned by nothing, and hiding that
 * loses the top of the tree.
 */
export interface PartRow extends EntityRow {
  is_definition: boolean
  is_contained: boolean
  /**
   * A generic kind: it may be SPECIALIZED but never used directly.
   *
   * SysML's own `abstract`, not a BEST invention - the library already declares
   * abstract requirement and attribute defs, and the modifier round-trips
   * through the parser, printer and projection. So `Sensor` and `Thruster` can
   * exist as base types carrying attributes, while a design is forbidden from
   * containing a bare `part x : Sensor`.
   */
  is_abstract: boolean
}

/**
 * One rolled-up quantity for one part.
 *
 * Four states, and rendering them as one number is the failure this shape
 * exists to prevent:
 *
 *  - a figure: `total` with `is_partial` false and `bound` `'exact'`
 *  - a FLOOR: `bound` `'at_least'`, because an unbounded multiplicity was
 *    crossed or something below states nothing. Render `>= 141.85 kg`
 *  - a CEILING: `bound` `'at_most'`, which only a `min` produces - a subtree
 *    with a value missing can only make the weakest link look STRONGER than it
 *    is. Render `<= 50 Gy`, and never `>=`: reading a ceiling as a floor is
 *    reading a shortfall as a margin
 *  - PARTIAL: something below states nothing, and `unstated` NAMES the parts
 *    responsible. Unset is not zero - a vehicle with three unfilled masses must
 *    not report a confident wrong number
 *  - unknowable: `total` is null, because nothing below it stated anything
 *
 * `problems` carries what could not be summed and why - a unit that disagrees,
 * a value nobody could parse. Surface it; swallowing it turns a refusal to
 * guess into a silent wrong answer.
 */
export interface PartTotal {
  /** This part's own stated value, apart from its children. */
  own: number | null
  total: number | null
  unit: string
  is_partial: boolean
  /** The parts below that state nothing, by name. */
  unstated: string[]
  /**
   * Which function combined the subtree, from `@RollsUp { how }` in the model.
   * A `min` is a weakest link, not a total, and must not be labelled one.
   */
  how: Aggregation
  bound: Bound
  problems: string[]
}

/**
 * One part's total against one budget, as the server settled it.
 *
 * FIVE STATUSES, and the reason there are five rather than a boolean is the one
 * rule this whole payload exists to carry: **a partial total can prove an
 * overrun and can never prove a margin.**
 *
 * A total over a subtree with something unstated is a FLOOR. A floor above the
 * budget is a proven overrun - what is missing can only add to it. A floor
 * below the budget proves nothing at all, and that is `indeterminate`, with
 * `margin` null BY CONSTRUCTION rather than by a screen declining to show it.
 *
 * A vehicle allowed 100 kg reading 70 with the flight software unstated does
 * not have 30 kg of headroom. That is the number somebody designs against, and
 * it is why nothing on this side may compute a margin of its own or colour an
 * `indeterminate` as a pass.
 */
export interface BudgetCheck {
  /** The profile feature, e.g. `dryMass`. */
  feature: string
  status: ComplianceStatus
  /** What the part is allowed, in `unit`, or null where nothing parsed. */
  budget: number | null
  /** What it has, or null where nothing below it states a value. */
  total: number | null
  unit: string
  /**
   * `budget - total`, and ONLY where the comparison is settled.
   *
   * Null on `indeterminate` and on `not_comparable`. Never recompute it here:
   * a margin derived from a floor is a number somebody will quote, and it is
   * wrong in the unsafe direction.
   */
  margin: number | null
  /**
   * Why the answer is what it is, in words.
   *
   * Load-bearing on `not_comparable`, which is ONE status covering more than
   * one refusal - a unit that disagrees, a budget on an aggregation that is not
   * a sum, or nothing stated to compare at all. The status says it was not
   * judged; only the note says why, so it has to reach the screen.
   */
  note: string
}

/**
 * What a budget check settled, and how strong the claim is.
 *
 *  - `over` - PROVEN. The total exceeds the budget, floor or not
 *  - `within` - PROVEN. An exact total, under budget, with a real margin
 *  - `indeterminate` - a floor under budget. Neither proven; no margin exists
 *  - `not_comparable` - nothing was compared, and `note` says why
 *  - `unbudgeted` - no figure. NEVER SENT: the server omits the key entirely,
 *    and it is named here only so the union matches the server's own five
 */
export type ComplianceStatus =
  | 'over'
  | 'within'
  | 'indeterminate'
  | 'not_comparable'
  | 'unbudgeted'

/**
 * A part's stated attribute values, by registry key, as WRITTEN.
 *
 * A string with its unit - `'310 [SI::kg⋅m²]'`, `'0.02 [SI::m]'` - not a number,
 * because a quantity is a value AND a unit and splitting them here would be
 * this side inventing a conversion. Absent or null is unset, which is not zero.
 *
 * `GET /api/parts/{id}` answers these alongside the named fields, one per
 * attribute the profile declares - which is where the system-level properties
 * arrive, both the ones that do not roll up and the ones that are not scalars.
 * A centre of gravity is `cg_x`/`cg_y`/`cg_z` and an inertia tensor is six
 * `inertia_i*` terms about a point that is itself three more. They are FLAT
 * because a value is stored at the feature path the model wrote; putting them
 * back together is `system-attributes.ts`'s job.
 *
 * ONLY A DEFINITION CARRIES THEM. A usage answers null for every one and names
 * its `definition` instead - the same rule the budgets follow, for the same
 * reason: the value is stated on the definition.
 */
export type StatedValues = Record<string, unknown>

/** How a rolling attribute combines its subtree. */
export type Aggregation = 'sum' | 'min' | 'max'

/** What a total's number is, relative to the truth. */
export type Bound = 'exact' | 'at_least' | 'at_most'

/**
 * A quantity that rolls up, as the model declares it.
 *
 * Read the columns from THIS rather than hard-coding "Dry mass": which
 * attributes aggregate is stated in the library by `@EchelonSysML::RollsUp`, so
 * a program adding one - timing, memory, delta-V - must appear without a change
 * on this side. That is the whole reason the marker lives in the model.
 */
export interface RollingAttribute {
  /** The column, and the key into `totals`. */
  name: string
  /** The profile feature it redefines, e.g. `dryMass`. */
  feature: string
  label: string
  unit: string
}

/**
 * One child to declare inside a part definition.
 *
 * A bare string is the definition alone; the object form adds the multiplicity,
 * which is the whole reason for adding a usage rather than pointing at a
 * definition twice. `'[4]'` means four of them; omitted means the language
 * default.
 *
 * The SAME rows are accepted by `child_parts` on `api.createPart` and by
 * `api.addChildParts`, deliberately, so one table component serves the wizard
 * and the add-a-child pane.
 */
export type ChildPartRow = string | { definition: string; multiplicity?: string }

/** One part usage declared inside a definition - a line of its decomposition. */
export interface ContainedPart {
  id: string
  name: string
  qualified_name: string
  type_name: string
  multiplicity: string
  is_definition: boolean
  /**
   * The definition this usage is typed by, already resolved.
   *
   * Null when the type names nothing the model declares. Resolved on the
   * server because `type_name` arrives written with package short names, so
   * matching it is not a string operation a front end should be inventing.
   */
  definition: string | null
  line: number
}

/**
 * One part in full, with its decomposition.
 *
 * `contains` is what makes the design walkable: a definition, the usages
 * declared inside it, and the definition each of those is typed by - so
 * definition to definition is two clicks rather than a guess. The flat listing
 * cannot express any of it.
 */
export interface PartDetail extends EntityRow {
  is_definition: boolean
  /** A generic kind - specializable, never usable directly. See PartRow. */
  is_abstract: boolean
  /** The part this one is declared inside, or null at the top. */
  owner: string | null
  /** For a usage, the definition it is typed by. */
  definition: string | null
  contains: ContainedPart[]
  /** Real hardware, marked `@EchelonSysML::Physical`. */
  is_physical: boolean
  /** Rolled-up quantities, keyed by column - `dry_mass`, `power_draw`, … */
  totals: Record<string, PartTotal>
  /** Which quantities roll up, for labelling columns without naming them here. */
  attributes: RollingAttribute[]
  /**
   * The attributes this part may STATE, by feature name.
   *
   * NOT the same list as `attributes`, and the difference matters in both
   * directions. An attribute belongs to the part def that declares it, so only
   * a power source may state a `powerGeneration` - offering it on a fastener is
   * a question with no answer. But a part TOTALS whatever its subtree carries:
   * a vehicle is not a power source and its power generation total is real, so
   * the rollup columns come from `attributes` and are not gated at all.
   *
   * Absent from an older server, which reads as "no gate" and offers everything.
   */
  statable?: string[]
  /**
   * The states this part can be in, by identifier: exhibited by the part or by
   * anything it sits inside, through usages as well as owners (issue #42).
   *
   * A guide for the state-value picker, not a gate - the server accepts any
   * state that resolves. Absent from an older server, which means no narrowing.
   */
  states?: string[]
  /**
   * Quantities the model says roll up but BEST cannot store.
   *
   * A program declaring its own rolling attribute has no column behind it, so
   * there is nothing to sum. Named rather than dropped: silently ignoring it
   * leaves somebody reading a model that says a quantity aggregates and a screen
   * that never mentions it.
   */
  unstorable: string[]
  /**
   * The ports declared on THIS part, in declaration order (issue #52). A
   * usage's ports are its definition's, which the pane reaches by resolving to
   * the definition. Absent from an older server.
   */
  ports?: PartPort[]
}

/** One port declared on a part: where something crosses its boundary. */
export interface PartPort {
  id: string
  name: string
  qualified_name: string
  type_name: string
  /** `~` on the type: every direction on the port def reversed. */
  is_conjugate: boolean
  multiplicity: string
}

/**
 * A feature an ICD element declares - what crosses the boundary (issue #52).
 *
 * `out item telemetry : Telemetry` on a port def; `end port producer :
 * TelemetryPort` on an interface def, which is what `is_end` marks.
 */
export interface InterfaceFeature {
  keyword: string
  /** `in`, `out`, `inout`, or empty for a feature that states none. */
  direction: string
  name: string
  type_name: string
  is_conjugate: boolean
  multiplicity: string
  is_end: boolean
}

/** A port as a connection or an endpoint names it. */
export interface ConnectionPort {
  identifier: string
  name: string
  qualified_name: string
  type_name: string
  is_conjugate: boolean
  /** The part the port is declared on. */
  on_part: string | null
}

/**
 * What an interface inside a part def may join: the def's own port, or a port
 * on the definition one of its usages is typed by.
 *
 * `path` is exactly what the connector will write - `tlmOut`, `sensor.tlmOut` -
 * and exactly what `connectPorts` takes back.
 */
export interface ConnectionEndpoint {
  path: string
  /** The usage the port is reached through; null for the def's own port. */
  usage: { identifier: string; name: string } | null
  port: ConnectionPort
  /** What the port's def declares, when its type resolves to one. */
  features: InterfaceFeature[]
}

/** One interface (or other connector) a part takes part in. */
export interface PartConnection {
  qualified_name: string
  identifier: string
  name: string
  /** `interface` or `connection` - the keyword it was declared with. */
  keyword: string
  type_name: string
  statement: string
  /** The part def it is written inside. */
  owner: string | null
  /**
   * True when written inside the part being asked about - it is the CONTEXT.
   * False when written elsewhere with an end on this part's own port - it is
   * one SIDE.
   */
  declared_here: boolean
  interface: { identifier: string; name: string; qualified_name: string } | null
  ends: {
    /** The interface def's end this binds, e.g. `producer`; empty when unnamed. */
    name: string
    reference: string
    part: { identifier: string; name: string } | null
    port: ConnectionPort | null
  }[]
}

/**
 * A usage the server created as part of building a definition.
 *
 * Read the NAME off this rather than deriving it. A usage carries no
 * `<ID>` - it is `part guidance : …::Guidance;`, named after its definition with
 * a lowered first letter - and the name is made unique MODEL-WIDE, so a second
 * usage of Guidance is `guidance2`. The loader falls back to the name when there
 * is no short name, which makes a usage's name its `id` on the wire; two
 * `guidance` rows would be two answers to /api/parts/guidance.
 */
export interface CreatedUsage {
  qualified_name: string
  name: string
  /** The definition this usage was declared inside. */
  owner: string
  /** The definition it is typed by. */
  definition: string
}

/**
 * What creating a part definition did.
 *
 * All or nothing: one transaction, one regeneration check, one commit. A failure
 * at any step leaves nothing behind, so a form may retry the same body unchanged
 * rather than working out how far it got.
 */
export interface CreatedPart {
  kind: string
  identifier: string
  name: string
  qualified_name: string
  created: boolean
  is_abstract: boolean
  /** The parent usage and each child usage, in the order they were made. */
  usages: CreatedUsage[]
}

/**
 * Where the suggestion box files, and whether it can file at all.
 *
 * `available` folds four different reasons into one answer - a sandbox, an
 * unconfigured GitLab, an anonymous caller and a read-only token - because
 * they are the same fact to the person looking at the screen: pressing the
 * button would fail. The reasons are the server's business.
 */
export interface FeedbackTarget {
  available: boolean
  /** The GitLab project feedback goes to, or null when there is none. */
  project: string | null
  /** Its issue list, for the "see what has already been raised" link. */
  url: string | null
  kinds: { value: string; label: string }[]
}

/** An issue the suggestion box raised. */
export interface RaisedIssue {
  iid: number
  title: string
  web_url: string
  labels: string[]
}

/** One commit on a branch. History, as GitLab reports it. */
export interface CommitRow {
  sha: string
  short_sha: string
  title: string
  message: string
  author_name: string
  authored_at: string
  web_url: string
}

/** The commit a work branch was cut from. A subset of `CommitRow`. */
export type BranchedFrom = Pick<
  CommitRow,
  'sha' | 'short_sha' | 'title' | 'authored_at' | 'web_url'
>

/**
 * A branch's history, and what kind of history it is.
 *
 * `scope` 'branch' is a work branch: `items` are only its own commits and
 * `branched_from` the commit it was cut from. 'default' is the default branch:
 * `items` are one page of the most recent commits, and `has_older` is whether
 * another page exists. There is no total - GitLab leaves it off a large
 * repository's commit listing.
 */
export interface CommitHistoryPayload {
  ref: string
  scope: 'branch' | 'default'
  default_branch: string
  page: number
  has_older: boolean
  branched_from: BranchedFrom | null
  items: CommitRow[]
}

/**
 * One field that differs between two versions of a row: name, before, after.
 *
 * A tuple rather than an object because that is what the API sends, and giving
 * it three keys here would mean naming them in two places for the sake of
 * reading `row[0]` as `row.field` once.
 */
export type FieldChange = [string, string, string]

/** What can have happened to a row between two refs. */
export type ChangeStatus = 'added' | 'removed' | 'modified'

/**
 * One element that moved on this branch.
 *
 * `uid` is the immutable id from issue #29 and is EMPTY for anything never
 * minted, which is most of a model. Absent, not a fault - so it is rendered
 * when there is one and nothing is said when there is not.
 */
export interface ElementChange {
  status: ChangeStatus
  key: string
  uid: string
  qualified_name: string
  kind: string
  keyword: string
  path: string
  line: number
  fields: FieldChange[]
  author: string
  changed_on: string
}

/**
 * One requirement whose PROJECTION changed - what drives the review list.
 *
 * `links_changed` with an empty `fields` is a real and important state: a
 * requirement that now derives from something else has every scalar column
 * identical, and nothing but this flag can say it moved.
 */
export interface RequirementChange {
  status: ChangeStatus
  identifier: string
  qualified_name: string
  name: string
  level: number | null
  fields: FieldChange[]
  links_changed: boolean
}

/**
 * One requirement to re-review, because something it links to changed.
 *
 * `because` is the changed requirement this hangs off and `via` is how it is
 * linked to it - 'source', 'derived' or 'related'. Both are rendered: a review
 * list that says only WHAT to look at, and not why, is a chore rather than an
 * instruction.
 */
export interface ReviewItem {
  identifier: string
  qualified_name: string
  name: string
  level: number | null
  depth: number
  because: string
  via: string
  path: string[]
}

/**
 * What a branch changes against what it would merge into.
 *
 * `stale` is not decoration. Neither side of the comparison is a ref - each is
 * BEST's working copy of one - so a target read before somebody else merged
 * into it reports their work as this branch's. The server says so and the
 * screen has to show it.
 */
export interface ModelChanges {
  ref: string
  target: string
  depth: number
  stale: string[]
  counts: {
    elements: Record<ChangeStatus, number>
    requirements: Record<ChangeStatus, number>
    review: number
  }
  elements: ElementChange[]
  requirements: RequirementChange[]
  review: ReviewItem[]
}

/** What merging from inside BEST reports back. */
export interface MergeResult {
  iid: number
  state: string
  web_url: string
  branch: string
  queued: boolean
}

/**
 * One declaration a paste of SysML would add, or added.
 *
 * `keyword` is how it is WRITTEN - 'part def', 'state def', 'import' - which is
 * the only description that is true of every member the parser accepts. There
 * is no entity kind here on purpose: pasted text may declare something BEST
 * stores and does not project, and calling that 'an element' is honest where
 * naming a kind would not be.
 */
export interface PastedDeclaration {
  name: string
  keyword: string
  is_definition: boolean
}

/**
 * The result of pasting SysML into a package, or of previewing one.
 *
 * `committed` is false for a dry run - and a dry run here proves more than a
 * CSV preview does: the text is parsed, grafted, projected and regenerated
 * before it is rolled back, so a preview that succeeds is a preview that did
 * the work.
 */
export interface SysmlPasteResult {
  destination: string
  path: string
  committed: boolean
  added: PastedDeclaration[]
}

/**
 * What one row of an imported CSV did.
 *
 * 'unchanged' is reported rather than skipped: a spreadsheet round trip sends
 * back every row, and "sixty rows, four changed" is the useful answer.
 */
export interface CsvRowOutcome {
  line: number
  identifier: string
  action: 'created' | 'updated' | 'unchanged' | 'failed'
  detail: string
  changed: string[]
}

/**
 * The result of a CSV import, or of previewing one.
 *
 * `committed` is false for a dry run, which is how the UI knows whether what it
 * is showing has happened yet. `counts` comes from the server rather than being
 * tallied here, so the headline and the rows cannot disagree.
 */
export interface CsvImportResult {
  committed: boolean
  ignored_columns: string[]
  missing_columns: string[]
  counts: Record<CsvRowOutcome['action'], number>
  rows: CsvRowOutcome[]
}

// `Facets` and `RequirementFilters` were here, and went with issue #35.
//
// Both described a filter bar that no longer exists: `Facets` was the shape of
// /api/facets, which filled its menus, and `RequirementFilters` was the set of
// query parameters it sent. Element tables filter in the browser now, and the
// grid's own column filters do the faceting - so `api.requirements` takes the
// same {search, limit, offset} as every other listing.

/**
 * A verification row, with the three columns its listing adds.
 *
 * `owner` DOES arrive on the listing, despite being a reference: `entity_json`
 * keeps the references that are `derived`, and `entity_page` eager-loads them
 * for the whole page in one query. An earlier note here claimed the opposite;
 * `api.py:listed_references` is the code that settles it.
 *
 * It is still not the whole answer for a flowdown. It is null on a definition
 * and populated on a usage, so the campaign -> plan and plan -> case hops go
 * through `type_name` resolution regardless - which is why it is a fallback
 * here rather than the edge a tree is built from.
 */
export interface VerificationRow extends EntityRow {
  verification_kind: 'case' | 'plan' | 'campaign'
  is_definition: boolean
  verifies: string | null
  owner: string | null
  /** A case's procedure prose (issues #105, #106); '' when not stated. */
  test_conditions?: string
  test_input?: string
  data_reduction?: string
  test_output?: string
  pass_fail_criteria?: string
  test_notes?: string
  /** A plan's. */
  hardware?: string
  instrumentation?: string
  safety?: string
}

/**
 * One transition out of a state.
 *
 * `from`/`to` are the resolved state's ID, falling back to the reference text
 * as written - an end may name something that resolves to no state in scope
 * (reported as a `dangling-transition` finding), and answering null there would
 * drop the edge from a diagram rather than draw it imprecisely.
 *
 * A transition carries no name and nothing in the language points at one, so
 * it is read through the state that declares it and written by naming that
 * state and its index there (issue #50) - there is no identifier to edit it by.
 */
export interface Transition {
  name: string
  from: string
  to: string
  from_reference: string
  to_reference: string
  trigger: string
  guard: string
  effect: string
  /** 'action' for `do action X`, '' for `do X`. Sent back so a save does not rewrite one as the other. */
  effect_keyword?: string
  /** The short name as declared - `<T_LAUNCH>` - or ''. `name` above is for display. */
  identifier?: string
  /** The name as declared, or ''. */
  declared_name?: string
  /**
   * The part that decides to take this edge - issue #42. Its identifier when
   * it resolved, the text otherwise; empty when unstated, which is optional.
   */
  decided_by: string
  decided_by_reference: string
  line: number
}

/** A state in full: the uniform shape, plus the transitions in its body. */
export interface StateRow extends EntityRow {
  /** Edges to other states only - never the entry successions below. */
  transitions: Transition[]
  /**
   * Where this machine starts: `entry; then X;` / `if G then X;` (issue #42).
   * Absent from an older server.
   */
  entries?: StateEntry[]
  /** True when this state is initial only because its machine states no entry. */
  initial_is_implicit?: boolean
  /**
   * Every edge that leaves / enters this state, WHEREVER it is declared (issue
   * #50). `transitions` is only what this state's body declares - nothing, for a
   * leaf - while a person on a state asks what goes in and out of it.
   */
  outgoing?: TouchingTransition[]
  incoming?: TouchingTransition[]
  /**
   * Which of a usage's lists are its DEF's, inherited because it states none of
   * its own (decision 030): `exhibited_by`, `includes`. Absent from an older server.
   */
  inherited?: string[]
  /** For a def: every usage of it, and the machine each is in. */
  usages?: StateUsage[]
}

/** What `POST /api/state-usages` answers. */
export interface StateUsageCreated {
  kind: 'state'
  identifier: string
  name: string
  qualified_name: string
  /** The def it is a usage of, by id. */
  definition: string
  /** The qualified name of the machine it went into. */
  machine: string
}

/** A state as the state map names it: by id, and by the def it is (a usage's def, or itself). */
export interface StateMapEnd {
  id: string
  name: string
  qualified_name: string
  definition: string
  definition_name: string
}

/** A state, or a machine, by id. */
export interface StateMapRef {
  id: string
  name: string
  qualified_name: string
}

/** One transition anywhere in the model, with the machine it is in. */
export interface StateMapTransition {
  /** Null when that end names no state the model has. */
  from: StateMapEnd | null
  to: StateMapEnd | null
  from_reference: string
  to_reference: string
  trigger: string
  guard: string
  effect: string
  decided_by: string
  /** The state whose body declares it. */
  declared_in: StateMapRef
  /** Its outermost machine. */
  machine: StateMapRef
}

/** `GET /api/state-map` (burndown Phase 8): every transition, every machine, how they link. */
export interface StateMap {
  transitions: StateMapTransition[]
  machines: (StateMapRef & { views: string[]; defs: string[] })[]
  /** `shares`: both machines hold usages of the defs in `via`; `includes`: a state of `from` includes those of `to`. */
  links: { from: string; to: string; kind: 'shares' | 'includes'; via: string[] }[]
}

/** What `POST /api/state-groups` answers: the machine, and the usage each state became. */
export interface StateGroupResult {
  /** The machine's qualified name. */
  machine: string
  usages: { state: string; usage: string; qualified_name: string }[]
}

/** One usage of a state def, in the machine that holds it. */
export interface StateUsage {
  id: string
  qualified_name: string
  /** The qualified name of the machine (or composite state) it is declared in. */
  machine: string
}

/**
 * An edge touching a state, with where it lives. A transition has no identity
 * (decision 008), so `owner` and `index` - plus its ends as written - are how a
 * change or a removal names it.
 */
export interface TouchingTransition extends Transition {
  /** The state whose body declares it: the innermost one enclosing both ends. */
  owner: string
  owner_name: string
  /** Its position among the owner's edges, from 0. */
  index: number
}

/**
 * The requirements linked to one element, and the links that could reach it
 * (issue #48) - `GET /api/links/{kind}/{identifier}`.
 */
export interface LinkReport {
  kind: string
  identifier: string
  /** Each requirement pointing here, and through which link. */
  requirements: {
    id: string
    name: string
    statement: string
    type_name: string
    feature: string
    /** The requirement field to PATCH to change or clear it. */
    field: string
    label: string
  }[]
  /** Every link that may reach this kind, and the requirements that may state it. */
  links: {
    feature: string
    field: string
    label: string
    /** The requirement types declaring it. */
    types: string[]
    requirements: string[]
  }[]
}

/**
 * One transition made WITH a new state (issue #50): the other end, and which
 * way it goes - 'to' leaves the new state, 'from' enters it.
 */
export interface StateTransitionRow {
  direction: 'to' | 'from'
  state: string
  trigger?: string
  guard?: string
  effect?: string
  decided_by?: string
}

/** One entry succession: the state a machine starts in, and the guard on it. */
export interface StateEntry {
  /** The state's identifier, falling back to the reference as written. */
  to: string
  to_reference: string
  /** Empty for the unconditional entry. */
  guard: string
  line: number
}

/** One `expose` of a view, as written and as resolved. `target` is '' when nothing answers. */
export interface ViewExpose {
  reference: string
  is_wildcard: boolean
  is_recursive: boolean
  target: string
  line: number
}

/** A state a view shows, carrying what a diagram needs of it and nothing more. */
export interface ViewState {
  id: string
  name: string
  qualified_name: string
  /** The identifier of the machine it is declared in, or null for a machine. */
  owner: string | null
  is_definition: boolean
  /** The id of the def a usage is typed by (decision 030); null for a def. Absent from an older server. */
  definition?: string | null
  is_initial: boolean
  statement: string
  transitions: Transition[]
  /** Indexes into the view's `exposes`: which of them bring this state in. */
  shown_by: number[]
  /**
   * CLIENT-SIDE ONLY: a diagram node standing for SysML's `start` or `done` end
   * of an action, which names no element. Never sent, never in a layout key
   * that could be mistaken for one - see `diagramKinds.ts`.
   */
  pseudo?: 'start' | 'done'
}

/** One succession as the API reads it: a transition without trigger, guard or effect. */
export interface Succession {
  name: string
  identifier: string
  declared_name: string
  from: string
  to: string
  from_reference: string
  to_reference: string
  line: number
}

/** A function or a step a view shows. A step is an `action` usage inside a function. */
export interface ViewFunction {
  id: string
  name: string
  qualified_name: string
  owner: string | null
  is_definition: boolean
  type_name: string
  statement: string
  successions: Succession[]
  shown_by: number[]
}

/** Where one element is drawn, in absolute diagram units. */
export interface LayoutBox {
  x: number
  y: number
  width: number
  height: number
}

/**
 * A view in full: a diagram as SysML states one (issue #44).
 *
 * `layout` is keyed by element QUALIFIED NAME, and may hold entries for elements
 * the view no longer shows, or lack entries for ones it does - both are normal.
 */
export interface ViewDetail extends EntityRow {
  exposes: ViewExpose[]
  states: ViewState[]
  /** Absent from a server older than issue #44 phase 5. */
  functions?: ViewFunction[]
  layout: Record<string, LayoutBox>
  /**
   * The one machine this view is PAIRED with - it exposes exactly `M::**`
   * (decision 030) - or null. A state put on a paired diagram becomes a usage
   * in it. Absent from an older server.
   */
  machine?: { id: string; name: string; qualified_name: string } | null
}

/**
 * One transition as it is WRITTEN, which is not the shape it is read in.
 *
 * A read carries `from` and `to` already resolved to state identifiers, with
 * the reference text alongside. A write states the references only: resolving
 * them is the server's job, and sending an id it had resolved for us would
 * make the file say something the author did not type.
 */
/**
 * One figure that holds only while a part is in one state.
 *
 * `value` and `budget` are independent and either may be empty - a part may
 * draw more in a state, be ALLOWED more in a state, or both. Stating NEITHER
 * is refused rather than dropped: a marker with no figure means "the baseline
 * applies here too", which is what having no annotation already says.
 *
 * The BASELINE is not in this list. It is the attribute's own value, and
 * repeating it here would give a reader two places to look for one figure.
 */
export interface StateValue {
  state: string
  value: string
  budget: string
  basis: string
}

export interface TransitionEdit {
  source: string
  target: string
  trigger?: string
  guard?: string
  effect?: string
  effect_keyword?: string
  identifier?: string
  name?: string
  /** A part's identifier or qualified name; the server writes it qualified. */
  decided_by?: string
}

/**
 * One program BEST hosts.
 *
 * The last three fields are this PERSON'S state in it, not the program's:
 * hiding is a nav preference and the branch is a bookmark. Neither is a
 * permission - everyone who can reach BEST can reach every program in it.
 */
export interface ProjectRow {
  slug: string
  name: string
  description: string
  repo_path: string
  /** True when the model is the root of its own repository (repo_path '.'). */
  at_repository_root?: boolean
  gitlab_project: string
  default_branch: string
  is_archived: boolean
  file_count: number
  last_imported_at: string | null
  last_imported_ref: string
  last_imported_sha: string
  hidden: boolean
  last_branch: string
  last_issue: string
}

/**
 * One branch in a program's repository.
 *
 * `is_protected` and `can_push` travel because BEST must not offer to write
 * where a push would be refused — better to say why than to fail at commit.
 */
export interface BranchRow {
  name: string
  sha: string
  is_default: boolean
  is_protected: boolean
  can_push: boolean
}

/**
 * One GitLab issue, as a piece of work to be done.
 *
 * `branch_name` is what BEST would create for it — shown before you commit to
 * anything, so starting work holds no surprise.
 *
 * `assignees` is usernames, shown as GitLab holds them. `merge_request` is set
 * only on the "review requested" listing, where each issue is there BECAUSE of
 * an open merge request that names the caller as a reviewer. Both are absent
 * from a payload that predates issue #67.
 */
export interface IssueRow {
  iid: number
  title: string
  state: string
  web_url: string
  labels: string[]
  branch_name: string
  assignees?: string[]
  merge_request?: ReviewRequest | null
}

/**
 * The merge request behind a "review requested" row.
 *
 * `source` is the branch it is from and `empty` whether it has any commits —
 * `null` when GitLab would not say. Issue #98: one opened from GitLab's issue
 * page onto a fresh `-2` copy of the branch is empty, and its Draft status
 * hides that. Both are absent from a payload that predates it.
 */
export interface ReviewRequest {
  iid: number
  title: string
  web_url: string
  source?: string
  empty?: boolean | null
}

/**
 * A merge request open for the same issue from a different branch (issue #98).
 *
 * `empty` is whether it has any commits, `null` when GitLab would not say.
 */
export interface OtherProposal {
  iid: number
  title: string
  web_url: string
  source: string
  empty: boolean | null
}

/** Somebody on GitLab, as the people picker and the merge card name them. */
export interface Person {
  id: number
  username: string
  name: string
}

/**
 * A person with GitLab's hint about merging.
 *
 * `can_merge` is read from the branch's protection rule, so it is a hint and
 * not a verdict: true = may merge into the default branch, false = may not,
 * null = GitLab's rule could not be resolved for them. Null is not "no".
 */
export interface PersonMergeHint extends Person {
  can_merge?: boolean | null
}

/**
 * Somebody GitLab names as an owner of the files a merge request changes.
 *
 * Read from GitLab's own approval rules for that merge request - BEST asks
 * GitLab and never reads CODEOWNERS - so there is no list of owners anywhere in
 * BEST. `sections` are the CODEOWNERS sections the person owns for THIS change
 * ("BALROG Flight Software"). `is_reviewer` is whether they have already been
 * asked to review it; `can_merge` is the same hint a reviewer carries. Owners
 * are asked, never required: nothing is enforced.
 */
export interface CodeOwner extends PersonMergeHint {
  sections: string[]
  is_reviewer: boolean
}

/** One entry of a project's member list. */
export interface Member extends PersonMergeHint {
  access_level: number
}

/**
 * Who a project's work and merge requests can be handed to.
 *
 * `me` is the signed-in person, whether or not they appear in `items`.
 * `merge_rule` names the branch `can_merge` is about and describes who may
 * merge into it, in GitLab's words; BEST relays it and holds no rule of its own.
 */
export interface MembersPayload {
  me: Person
  merge_rule: { branch: string; description: string }
  items: Member[]
}

/**
 * What starting work produced: an issue, its branch, and maybe an import.
 *
 * `assignees` (usernames) sits beside `issue`, not inside it - that is where
 * the server puts it. Absent from a payload that predates issue #67.
 */
export interface WorkItem {
  issue: {
    iid: number
    title: string
    state: string
    web_url: string
    created: boolean
  }
  branch: {
    name: string
    sha: string
    created: boolean
    can_push: boolean
  }
  imported: ImportResult | null
  assignees?: string[]
}

/**
 * GitLab's approval state for the open merge request (issue #99).
 *
 * `user_can_approve` and `user_has_approved` are GitLab's own answers for the
 * signed-in person, and they are the ONLY things that decide whether Approve or
 * Revoke is offered: BEST holds no rule about who may approve, authors
 * approving their own work included. Null means GitLab did not say - which is
 * not "no" - and then neither button is drawn.
 */
export interface ApprovalState {
  approved_by: Person[]
  /** How many approvals the rules ask for; 0 when none are required. */
  required: number
  /** How many are still missing, as GitLab counts them; null if it did not say. */
  left: number | null
  user_has_approved: boolean | null
  user_can_approve: boolean | null
}

/** What approving or revoking answered: the approvals as GitLab now holds them. */
export interface ApprovalResult {
  iid: number
  branch: string
  approvals: ApprovalState | null
}

/**
 * A merge request BEST opened, or found already open.
 *
 * `warnings` is the field to read rather than to log. It carries things that
 * are true and unwelcome and that GitLab reports nowhere - chiefly that the
 * `Closes #6` in the description will not fire, because GitLab acts on a
 * closing pattern only when the merge request targets the repository's own
 * default branch. Nothing in GitLab's UI shows that; the issue simply stays
 * open forever.
 *
 * `has_conflicts` is a tri-state: GitLab does not always know yet, and null
 * means "not answered", not "no". It is also TRUE for any merge request that
 * cannot merge - an empty one included - because GitLab defines it as a mirror
 * of its deprecated merge status. Nothing new should be decided from it; read
 * `merge_status`, which names the actual blocker.
 */
export interface MergeRequest {
  iid: number
  title: string
  web_url: string
  state: string
  source: string
  target: string
  /** True when this call opened it rather than finding one already open. */
  created: boolean
  draft: boolean
  has_conflicts: boolean | null
  /**
   * GitLab's `detailed_merge_status`, exactly as sent: `mergeable`, `conflict`,
   * `ci_still_running`, `not_approved`, ... The set grows over time, so this is
   * a string and not a union. Null when GitLab did not say; absent from a
   * payload that predates the field.
   */
  merge_status?: string | null
  /**
   * Whether the signed-in person may merge this into its target, as GitLab
   * reports it. Null when GitLab did not say - which is not "no" - and only the
   * open merge request is ever asked.
   */
  can_merge?: boolean | null
  /**
   * Who the merge request is assigned to, and who was asked to review it.
   * Absent from a payload that predates issue #67, which reads as nobody.
   * A reviewer's `can_merge` is the same hint a member carries.
   */
  assignees?: Person[]
  reviewers?: PersonMergeHint[]
  /** Who opened it; null or absent when GitLab did not say. */
  author?: Person | null
  /**
   * The owners GitLab names for what this merge request changes. Absent or
   * empty means none are known - a repository with no CODEOWNERS, a GitLab that
   * lists none, or a failed read - and is never a fault.
   */
  owners?: CodeOwner[]
  /**
   * The head commit the merge request had when BEST read it. Sent back on
   * Approve so nobody approves a head that moved after they looked. Absent from
   * a payload that predates issue #99; '' when GitLab did not say.
   */
  head_sha?: string
  /** Only the open merge request is asked; null or absent reads as no information. */
  approvals?: ApprovalState | null
  labels: string[]
  warnings: string[]
}

/**
 * Whether a branch has been proposed, and whether it has landed.
 *
 * Both are reported, and either may be null. `merged` is the only way to tell
 * that work landed: BEST opens merge requests with remove_source_branch, so
 * once one merges the branch is gone and there is nothing else left to look
 * at. Both null means the work has not been proposed yet.
 */
export interface MergeState {
  branch: string
  target: string
  open: MergeRequest | null
  merged: MergeRequest | null
  /**
   * Merge requests open for this work's issue from OTHER branches. Absent from
   * a payload that predates issue #98.
   */
  others?: OtherProposal[]
}

/**
 * What clearing up after a merge actually did.
 *
 * `warnings` is where a failed re-import lands, and where "the branch is gone
 * from GitLab with no merge request behind it" lands. The clearing up already
 * happened by then, so none of it reads as a failure.
 */
export interface FinishResult {
  branch: string
  merged: boolean
  merge_request: MergeRequest | null
  /** True when the branch's working database was deleted. */
  reaped: boolean
  /**
   * Anyone ELSE who was on the branch and has been put back on the default
   * one. Their working copy went with the database, so this is reported to
   * the person who caused it rather than left for them to discover.
   */
  moved: string[]
  refreshed: ImportResult | null
  warnings: string[]
}

/** What an import read, and what it wrote. */
export interface ImportResult {
  ref: string
  sha: string
  files: number
  requirements: number
  verification: number
  paths: string[]
  errors: Array<{ path: string; detail: string }>
}

/**
 * What this session would work against.
 *
 * `project` is null in single-database mode, where BEST was started against an
 * explicit database URL and there is no registry entry describing it. The
 * picker hides itself rather than offering a choice that does not exist.
 */
export interface CurrentProject {
  project: ProjectRow | null
  branch: string
  issue?: string
  single_database: boolean
}

/**
 * Find one kind by name or by plural.
 *
 * Both are accepted for the same reason the back end accepts both: a URL
 * segment is the plural while everything else says the singular, and making
 * callers remember which is a needless trap.
 */
export function findKind(schema: Schema, nameOrPlural: string): KindSchema | undefined {
  return schema.kinds.find((k) => k.name === nameOrPlural || k.plural === nameOrPlural)
}

/**
 * Turn 'verified_by' into 'Verified by', for a column or control the registry
 * has no label for.
 *
 * Here rather than beside the requirements grid it started in, because a
 * second caller appeared (the quick filters) and a third copy of this three-line
 * rule is how two screens come to call one field by different names.
 */
export function heading(name: string): string {
  const words = name.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** Whether a field's value has to be fetched before it can be offered. */
export function needsChoices(field: FieldSchema): boolean {
  return field.kind === 'enum'
}

/** Whether a field is one the listing endpoint leaves out to save a query. */
/**
 * The attribute a field binds, as a whole thing rather than a leaf.
 *
 * `operationalTemp.minTemp` and `operationalTemp.maxTemp` are two fields and ONE
 * attribute, and the model is what says so - the feature PATH is its own
 * statement of the structure. Matching the leaf would report a temperature band
 * as two unrelated quantities and, worse, would fail to match the server, which
 * names the attribute rather than its parts.
 *
 * Here rather than beside the parts screens because the generic entity form
 * needs it too, and a form that imports from `components/parts` to render a
 * requirement is a layering nobody meant.
 *
 * `''` for a field that binds no feature - a projection column, which belongs to
 * every row by construction and is never gated.
 */
export function attributeOf(field: FieldSchema): string {
  return (field.feature || '').split('.')[0]
}

/**
 * The fields a part of this type may state, filtered from a payload's `statable`.
 *
 * An attribute belongs to the part def that DECLARES it, so only a power source
 * may state a `powerGeneration` and offering it on a fastener is a question with
 * no answer. `statable` is the server's answer for one part, read from the model.
 *
 * Not only parts: requirements, interfaces and every kind the generic detail
 * route serves send one too (issue #85, a campaign offered a case's fields).
 *
 * Anything that is not an array of strings means NO GATE, which is what the
 * server sends when it cannot tell what an element's types declare, and what an
 * older server sends: too many fields beats a missing one.
 *
 * @param fields the kind's fields
 * @param statable `statable` from the payload, or whatever arrived in its place
 */
export function statableFields(
  fields: readonly FieldSchema[],
  statable: unknown,
): FieldSchema[] {
  if (!Array.isArray(statable) || statable.some((entry) => typeof entry !== 'string')) {
    return [...fields]
  }
  const allowed = new Set(statable as string[])
  return fields.filter((field) => !field.feature || allowed.has(attributeOf(field)))
}

/** The leaf of a feature path: `minTemp` of `operationalTemp.minTemp`. */
export function leafOf(field: FieldSchema): string {
  const path = field.feature || ''
  return path.includes('.') ? path.slice(path.lastIndexOf('.') + 1) : ''
}

/**
 * A heading for a set of fields, from what their labels share.
 *
 * `Inertia Ixx`, `Inertia Iyy` and `Inertia datum x` share `Inertia`, which is
 * the attribute and therefore the heading. Derived from the labels the profile
 * generated rather than tabulated anywhere, so a structure added to the library
 * gets a heading with nothing here being edited - and it falls back to the first
 * label when they share nothing, which is a heading a person can still read.
 */
export function groupLabelOf(fields: readonly FieldSchema[]): string {
  if (fields.length === 1) return fields[0].label
  const words = fields.map((field) => field.label.split(/\s+/))
  const shortest = Math.min(...words.map((parts) => parts.length))
  const common: string[] = []
  for (let at = 0; at < shortest; at += 1) {
    const word = words[0][at]
    if (!words.every((parts) => parts[at] === word)) break
    common.push(word)
  }
  return common.length > 0 ? common.join(' ') : fields[0].label
}

/** What to call one field WITHIN its group: `Ixx`, `x`, `datum x`, `min`. */
export function subLabelOf(field: FieldSchema, groupLabel: string): string {
  const trimmed = field.label.startsWith(groupLabel)
    ? field.label.slice(groupLabel.length).trim()
    : field.label
  return trimmed || leafOf(field)
}

/** One attribute's fields, gathered back into the thing the model wrote. */
export interface FieldGroup {
  /** The attribute, or the lone field's name where it binds no feature. */
  key: string
  label: string
  fields: FieldSchema[]
  /** True where the model declares more than one field under this attribute. */
  isStructured: boolean
}

/**
 * Fields grouped by the attribute they belong to.
 *
 * A centre of gravity is three fields and ONE fact; an inertia tensor is six
 * about a point that is three more; a temperature band is two ends. Given a
 * control each they were fifteen rows of a form for four facts, and a reader had
 * to reassemble each one by eye.
 *
 * Grouping is by the first segment of the FEATURE PATH, which is the model's own
 * statement that two fields are parts of one thing - never by a list here, and
 * never by the field names, which only look groupable by coincidence.
 *
 * ADJACENT fields only, so the profile's declaration order still fixes the order
 * and a group cannot silently gather two fields from opposite ends of the form.
 */
export function groupByAttribute(fields: readonly FieldSchema[]): FieldGroup[] {
  const groups: FieldGroup[] = []
  for (const field of fields) {
    const attribute = attributeOf(field)
    const last = groups[groups.length - 1]
    // A field with no feature never groups: two of them would otherwise share
    // the empty-string key and merge into one nonsense row.
    if (attribute && last && last.key === attribute) {
      last.fields.push(field)
      continue
    }
    groups.push({ key: attribute || field.name, label: field.label, fields: [field], isStructured: false })
  }
  for (const group of groups) {
    group.label = groupLabelOf(group.fields)
    group.isStructured = group.fields.length > 1
  }
  return groups
}

/**
 * The fields worth editing on THIS row, beyond what its type allows.
 *
 * One rule so far, and it is a modelling point rather than a layout one:
 * MULTIPLICITY characterises a usage inside its parent - four of these
 * thrusters - so a DEFINITION has none to state. The full editor offered the
 * box anyway, on every part definition, where filling it in would mean nothing.
 * The definitions table and the create wizard have both excluded it for the
 * same reason; this is the third place that needed to.
 *
 * A usage keeps it: that is exactly where the fact lives.
 *
 * @param fields the fields already narrowed by `statableFields`
 * @param row the entity being edited
 */
export function editableFields(
  fields: readonly FieldSchema[],
  row: Record<string, unknown>,
): FieldSchema[] {
  // Only a part says whether it is a definition; every other kind leaves this
  // undefined and keeps all of its fields.
  if (row.is_definition !== true) return [...fields]
  return fields.filter((field) => field.kind !== 'multiplicity')
}

/** The last segment of a qualified name: `Foo` of `A::B::Foo`. */
function tailOf(name: string): string {
  return name.split('::').pop() ?? ''
}

/**
 * The declared type a row is typed by, or null.
 *
 * Matched on the LAST SEGMENT, which is the rule the whole back end uses for a
 * type reference. It has to be: the schema offers a type by its fully qualified
 * name, because that is what has to be written into the `.sysml` for it to
 * resolve from a sibling package - and a row carries the type as the MODEL
 * wrote it, which is usually shorter (`RequirementTypes::SubsystemMassRequirement`).
 * Comparing them whole finds nothing.
 */
export function typeChoiceOf(kind: KindSchema, typeName: unknown): TypeChoice | null {
  const wanted = tailOf(String(typeName ?? ''))
  if (!wanted) return null
  return kind.types.find((entry) => tailOf(entry.name) === wanted) ?? null
}

/**
 * The same fields, with any this ROW generates marked `derived`.
 *
 * MARKS rather than filters, unlike its neighbours here - a generated field is
 * still shown, because its value is the useful thing on the screen; it is only
 * not editable.
 *
 * Per ROW and not per kind, which is why it cannot be a flag on the registry's
 * Field: whether a requirement's statement is generated depends on the type
 * THAT requirement is typed by, and the same kind serves both. An L3Requirement
 * keeps an author-written statement; a SubsystemMassRequirement renders one from
 * its values on every load, so a box accepting an edit would take an answer the
 * next load throws away. The API refuses that write, which is what makes the
 * `derived` marking honest rather than decorative.
 *
 * By field KIND, never by name: the doc field is the statement on a requirement
 * and the description on a part, and a type that templated either wants the
 * same treatment.
 */
export function generatedFields(
  fields: readonly FieldSchema[],
  kind: KindSchema,
  row: Record<string, unknown>,
): FieldSchema[] {
  const chosen = typeChoiceOf(kind, row.type_name)
  if (!chosen?.template) return [...fields]
  return fields.map((field) =>
    field.kind === 'doc'
      ? {
          ...field,
          derived: true,
          help: `Generated from ${tailOf(chosen.name)}'s template: ${chosen.template}`,
        }
      : field,
  )
}

export function isCostly(field: FieldSchema): boolean {
  return field.is_list || field.kind === 'reference'
}

/** Where in the code a comment thread sits, for the ones GitLab anchored to a diff. */
export interface DiscussionPosition {
  old_path: string | null
  new_path: string | null
  old_line: number | null
  new_line: number | null
}

/** One comment. `body` is what a person typed: show it as text and never as markup. */
export interface DiscussionNote {
  id: number | null
  author: { username: string; name: string }
  body: string
  /** The body was longer than BEST shows; the link has the whole of it. */
  truncated: boolean
  created_at: string
  updated_at: string
  /** The note on GitLab's merge request page; '' when not known. */
  web_url: string
  resolved: boolean
}

/**
 * A comment thread on the open merge request (issue #99, phase B).
 *
 * `position` is null for a general comment. `resolvable` is false for a loose
 * note, which has nothing to resolve; `outdated` is present only when GitLab said.
 */
export interface DiscussionThread {
  id: string
  resolvable: boolean
  resolved: boolean
  /** Username of whoever resolved it; '' while open. */
  resolved_by: string
  position: DiscussionPosition | null
  /**
   * The uid of the element the thread names (phase C), null for a thread about
   * no element in particular. Absent from a payload that predates phase C.
   */
  element?: string | null
  outdated?: boolean
  notes: DiscussionNote[]
  /** More notes than BEST shows. */
  truncated: boolean
}

/**
 * What GET /merge/discussions answers. `iid` is null when no merge request is
 * open, with nothing listed. `unresolved` counts every open resolvable thread,
 * including any past the cap.
 */
export interface DiscussionList {
  iid: number | null
  web_url: string
  threads: DiscussionThread[]
  unresolved: number
  truncated: boolean
}

/**
 * What POST /merge/element-comments answers (issue #99, phase C).
 *
 * `anchored` is false when GitLab would not take the comment on a line and it
 * went in as an ordinary thread instead; `notice` then says so and is shown to
 * the person. It is '' when the comment landed where it was asked to.
 */
export interface ElementCommentResult {
  iid: number
  anchored: boolean
  notice: string
  thread: DiscussionThread
}

/**
 * A program baseline: a protected tag cut from the default branch (issue #97).
 *
 * `trailers` are the lines the tag message ends with, recording what cut it:
 * `BEST-Version` and `Echelon-Library` are absent on a baseline cut before BEST
 * wrote them. `opened` is whether BEST has a read-only copy of it (phase D).
 * Everything here was written by people or by GitLab: draw it as text.
 */
export interface Baseline {
  name: string
  sha: string
  committed_at: string
  author: string
  notes: string
  trailers: { 'BEST-Version'?: string; 'Echelon-Library'?: string }
  web_url: string
  opened: boolean
}

/** What GET /baselines answers, newest first. `prefix` is what a baseline's tag name starts with. */
export interface BaselineList {
  items: Baseline[]
  prefix: string
  truncated: boolean
}

/** One CI job of a baseline's pipeline, with the artifacts it kept. */
export interface BaselineJob {
  id: number
  name: string
  status: string
  web_url: string
  browse_url: string
  download_url: string
  /** GitLab has deleted the artifacts; the job is listed so that is visible. */
  expired: boolean
  artifacts: { filename: string; size: number }[]
}

/** What GET /baselines/{tag}/artifacts answers. `pipeline` is null when none ran for the tag. */
export interface BaselineArtifacts {
  tag: string
  pipeline: { id: number; status: string; web_url: string } | null
  jobs: BaselineJob[]
}

/** What POST /baselines answers (201): the tag BEST named and made. `notice` may be ''. */
export interface BaselineCut {
  name: string
  sha: string
  web_url: string
  notes: string
  trailers: { 'BEST-Version'?: string; 'Echelon-Library'?: string }
  notice: string
}

/**
 * The 409 `main_moved` answer to a cut: the default branch has moved since the
 * person's copy of it was imported, so they must say which version to tag.
 */
export interface BaselineMainMoved {
  detail: string
  imported_sha: string
  current_sha: string
}

/** Where an opening baseline is: a phase, and a count when the phase has one. */
export interface BaselineProgress {
  phase: string
  done?: number
  total?: number
}

/**
 * What BEST knows about a baseline it has opened (issue #97, phase D).
 *
 * `library` says which Echelon library the view is built with: `pinned` is the
 * version the baseline was cut under; `archive-missing` and `unpinned` are read with
 * today's library (the copy is gone, or the tag predates the record). `shown_with`
 * is today's BEST. Everything here may be shown to a person: draw it as text.
 */
export interface BaselineInfo {
  sha: string
  cut_with: string | null
  shown_with: string
  library: 'pinned' | 'archive-missing' | 'unpinned'
  library_note: string
}

/** What POST .../baselines/{tag}/open and GET .../status answer. */
export interface BaselineStatus {
  tag: string
  ref: string
  state: 'not_opened' | 'opening' | 'ready' | 'failed'
  message: string
  started_at: string | null
  progress: BaselineProgress | null
  info: BaselineInfo | null
}

/**
 * The 409 `baseline_not_opened` answer to a comparison: one side is a baseline
 * BEST has not read yet, and `tag` says which, so the screen can offer to open it.
 */
export interface BaselineNotOpened {
  detail: string
  tag: string
}
